import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { useAuth } from "./AuthContext";
import { Visit, Supervisor } from "@visitproof/types";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";

interface MonitorContextProps {
  supervisor: Supervisor | null;
  supervisorId: string | null;
  visits: Visit[];
  upcomingVisits: Visit[];
  todayVisits: Visit[];
  pastVisits: Visit[];
  loading: boolean;
  error: string | null;
  refreshVisits: () => void;
}

const MonitorContext = createContext<MonitorContextProps | undefined>(undefined);

export const useMonitor = (): MonitorContextProps => {
  const context = useContext(MonitorContext);
  if (!context) {
    throw new Error("useMonitor must be used within a MonitorProvider");
  }
  return context;
};

export const MonitorProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { claims, loading: authLoading } = useAuth();
  const [supervisor, setSupervisor] = useState<Supervisor | null>(null);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [supervisorLoading, setSupervisorLoading] = useState<boolean>(false);
  const [visitsLoading, setVisitsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Get supervisor ID from AuthContext claims (per-account structure)
  const accountId = claims?.accountId || null;
  const supervisorId = accountId ? claims?.accounts?.[accountId]?.supervisorId || null : null;

  const refreshVisits = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  // Load supervisor profile when we have a supervisor ID
  useEffect(() => {
    if (authLoading || !supervisorId) {
      setSupervisor(null);
      setSupervisorLoading(false);
      return;
    }

    setSupervisorLoading(true);

    const supervisorDocRef = doc(db, "supervisors", supervisorId);
    const unsubscribe = onSnapshot(
      supervisorDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setSupervisor({
            id: docSnapshot.id,
            ...docSnapshot.data(),
          } as Supervisor);
        }
        setSupervisorLoading(false);
      },
      (err) => {
        console.error("Error loading supervisor:", err);
        setError(err.message);
        setSupervisorLoading(false);
      }
    );

    return () => unsubscribe();
  }, [authLoading, supervisorId]);

  // Load visits for the supervisor
  useEffect(() => {
    // Only load visits when we have a supervisorId and accountId
    if (authLoading || !supervisorId || !accountId) {
      setVisits([]);
      setVisitsLoading(false);
      return;
    }

    setVisitsLoading(true);

    // Query visits where supervisorId and accountId match
    const visitsQuery = query(
      collection(db, "visits"),
      where("accountId", "==", accountId),
      where("supervisorId", "==", supervisorId),
      orderBy("startDate", "asc")
    );

    const unsubscribe = onSnapshot(
      visitsQuery,
      (snapshot) => {
        const visitList = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as Visit[];
        setVisits(visitList);
        setVisitsLoading(false);
      },
      (err) => {
        console.error("Error loading visits:", err);
        setError(err.message);
        setVisitsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [authLoading, supervisorId, refreshKey]);

  // Combined loading state
  const loading = authLoading || supervisorLoading || visitsLoading;

  // Computed visit lists - memoized to avoid recalculating on every render
  const { todayVisits, upcomingVisits, pastVisits } = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

    const today = visits.filter((visit) => {
      const visitDate = visit.startDate?.toDate?.();
      if (!visitDate) return false;
      return visitDate >= startOfToday && visitDate <= endOfToday;
    });

    const upcoming = visits.filter((visit) => {
      const visitDate = visit.startDate?.toDate?.();
      if (!visitDate) return false;
      return visitDate > endOfToday && visit.status !== "canceled" && visit.status !== "completed";
    });

    const past = visits.filter((visit) => {
      const visitDate = visit.endDate?.toDate?.();
      if (!visitDate) return false;
      return visitDate < startOfToday;
    });

    return { todayVisits: today, upcomingVisits: upcoming, pastVisits: past };
  }, [visits]);

  return (
    <MonitorContext.Provider
      value={{
        supervisor,
        supervisorId,
        visits,
        upcomingVisits,
        todayVisits,
        pastVisits,
        loading,
        error,
        refreshVisits,
      }}
    >
      {children}
    </MonitorContext.Provider>
  );
};
